import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Modal,
  Fade,
  Backdrop,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  InputLabel,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemText
} from '@material-ui/core';
import PropTypes from 'prop-types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { diskSizes } from './LRUSize';
import PersistData from './PersistData';
import { VMGeneralSettings, ValidVMName } from './VMGeneralSettings';
import { VMResourceSettings } from './VMResourceSettings';
import { VMInitScriptSettings } from './VMInitScriptSettings';
import { ValidateDiskSize, VMDiskSettings } from './VMDiskSettings';
import { VMNetworkSettings } from './VMNetworkSettings';
import useVrackApi from '../../../hooks/useVrackApi';

export const VM_SELECT_TEST_ID = 'vm-select';
export const NEW_VM_BUTTON_TEST_ID = 'new-vm-modal-add-vm';
export const VM_SELECT_LABEL = 'Select a VM Template';
export const NEW_VM_MODAL_TITLE = 'Add New VM';

// returns a set of default vm values
function getDefaultVMValues() {
  return {
    replicas: 1,
    cpu: 2000,
    memory: 2000,
    size: diskSizes[0]
  };
}

export const diskSizeMap = {
  small: 50,
  medium: 100,
  large: 500,
  xlarge: 1000
};

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  p: 4
};

NewVMModal.propTypes = {
  rack: PropTypes.object,
  updateRack: PropTypes.func,
  isOpen: PropTypes.bool,
  closeHandler: PropTypes.func
};

export function NewVMModal({ rack, updateRack, isOpen, closeHandler }) {
  const [currentVM, setCurrentVM] = useState(null);
  // used to enable/disable only the add VM button
  const [addVMDisabled, setAddVMDisabled] = useState(false);
  const [availableVMs, setAvailableVMs] = useState([]);

  const { fetchData: fetchVMTemplates } = useVrackApi('get', '/racks/vm');
  const identity = useSelector((state) => state.identity.value);

  // will handle when an LRU is selected from the dropdown
  const handleSelectVMChange = (event) => {
    const vmIndex = event.target.value;
    setCurrentVM(availableVMs[vmIndex]);
  };

  const setVMPersist = (event) => {
    // update persistence for vm
    const s = { ...currentVM };

    s.persistData = event.target.value;
    setCurrentVM(s);
  };

  const addVM = () => {
    const rackCopy = { ...rack };
    const newRackUpdate = {
      ...currentVM,
      resources: {
        limits: {
          cpu: `${currentVM.cpu}m`,
          memory: `${currentVM.memory}M`
        },
        requests: {
          cpu: `${currentVM.cpu}m`,
          memory: `${currentVM.memory}M`
        }
      }
    };
    rackCopy.rack.vms.push(newRackUpdate);
    updateRack(rackCopy);

    // clear the current lru after adding
    setCurrentVM(null);
    closeHandler();
  };

  useEffect(() => {
    // queries the list of vm templates from the vrack-api
    fetchVMTemplates({
      params: { identity, global: true }
    })
      .then((response) => {
        const vmTemplates = response.map((v) => ({
          // add some default values
          ...getDefaultVMValues(),
          ...v
        }));
        setAvailableVMs(vmTemplates);
      })
      .catch((error) => {
        console.debug(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentVM) {
      const { isValid } = ValidVMName(currentVM.name, rack.rack.vms || []);
      const isDisksValid = currentVM.disks?.every((disk) => ValidateDiskSize(disk));
      setAddVMDisabled(!isValid || !isDisksValid);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVM]);

  return (
    <Modal
      open={isOpen}
      sx={{ outline: 'none' }}
      aria-labelledby="add-new-vm"
      aria-describedby="modal to add new vm"
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500
      }}
    >
      <Fade in={isOpen}>
        <Grid direction="row" sx={style} container>
          <Grid item xs={12} md={1} lg={2} onClick={closeHandler} />
          <Grid item xs={12} md={10} lg={8}>
            <Card>
              <CardHeader sx={{ padding: 2 }} title={NEW_VM_MODAL_TITLE} />
              <Divider />
              <CardContent sx={{ paddingTop: 1, maxHeight: '70vh', overflow: 'auto' }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid container spacing={2} item xs={12}>
                    <Grid item mt={2} xs={12}>
                      <FormControl>
                        <InputLabel id="lru-select-label">{VM_SELECT_LABEL}</InputLabel>
                        <Select
                          labelId="lru-select-label"
                          label={VM_SELECT_LABEL}
                          data-testid={VM_SELECT_TEST_ID}
                          onChange={handleSelectVMChange}
                        >
                          {availableVMs.length === 0 ? (
                            <MenuItem disabled value="">
                              No Valid VMs to Add
                            </MenuItem>
                          ) : (
                            availableVMs.map((vm, vmIndex) => (
                              <MenuItem key={vm.name} value={vmIndex}>
                                <ListItemText
                                  primary={vm.label}
                                  secondary={vm.name}
                                  sx={{
                                    '& span': { fontSize: '10pt' },
                                    '& p': { fontSize: '8pt' }
                                  }}
                                />
                              </MenuItem>
                            ))
                          )}
                        </Select>
                        <FormHelperText>
                          The type of VM you want to add to your test environment
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  </Grid>
                  {availableVMs.length > 0 && currentVM ? (
                    <>
                      <Grid container item spacing={2} xs={12}>
                        <VMGeneralSettings
                          currentVM={currentVM}
                          updateCurrentVM={setCurrentVM}
                          addedVMList={rack.rack.vms}
                          setAddVMDisabled={setAddVMDisabled}
                        />
                      </Grid>
                      <Grid container item spacing={2} xs={12}>
                        <VMResourceSettings currentVM={currentVM} updateCurrentVM={setCurrentVM} />
                      </Grid>
                      <Grid container item spacing={2} xs={12}>
                        <VMNetworkSettings currentVM={currentVM} updateCurrentVM={setCurrentVM} />
                      </Grid>
                      <Grid container item spacing={2} xs={12}>
                        <VMDiskSettings
                          currentVM={currentVM}
                          updateCurrentVM={setCurrentVM}
                          setAddVMDisabled={setAddVMDisabled}
                          vmTemplates={availableVMs}
                        />
                      </Grid>
                      <Grid container item spacing={2} xs={12}>
                        <VMInitScriptSettings
                          currentVM={currentVM}
                          updateCurrentVM={setCurrentVM}
                        />
                      </Grid>
                      <Grid container item spacing={2} sm={12}>
                        <Grid item xs={12}>
                          <Accordion>
                            <AccordionSummary
                              sx={{ paddingLeft: 0 }}
                              expandIcon={<ExpandMoreIcon />}
                            >
                              <Typography variant="h6">Advanced Settings</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid item xs={12}>
                                <PersistData currentLRU={currentVM} setLRUPersist={setVMPersist} />
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                      </Grid>
                    </>
                  ) : null}
                </Grid>
              </CardContent>
              <Divider />
              <Box sx={{ padding: 2, textAlign: 'right' }}>
                <Button
                  sx={{ marginLeft: 1 }}
                  color="secondary"
                  variant="outlined"
                  onClick={closeHandler}
                >
                  Cancel
                </Button>
                <Button
                  data-testid={NEW_VM_BUTTON_TEST_ID}
                  sx={{ marginLeft: 1 }}
                  color="primary"
                  variant="contained"
                  onClick={addVM}
                  disabled={addVMDisabled}
                >
                  Add VM
                </Button>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} med={1} lg={2} onClick={closeHandler} />
        </Grid>
      </Fade>
    </Modal>
  );
}
